import { Button, Stack, TextField, Typography, useTheme } from "@mui/material";
import deepEqual from "fast-deep-equal";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import PhoneTextField from "@entrilia/shared/components/inputs/PhoneTextField";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import { combineValidators, emailValidator, requiredValidator } from "@entrilia/shared/utilities/validators";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";

interface Props {
  settings: SupportInfoSettings;
}

interface SupportInfoSettings {
  supportEmail: string;
  supportPhone: string;
}

const updateInvestorPortalSettings = withErrorHandling(adminApi.updateInvestorPortalSettings);

const validateEmail = combineValidators(requiredValidator, emailValidator);

const SupportInfoEditor = ({ settings: originalSettings }: Props) => {
  const theme = useTheme();
  const { hasPermissions } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [settings, setSettings] = useState<SupportInfoSettings>(originalSettings);
  const [savedSettings, setSavedSettings] = useState<SupportInfoSettings>(originalSettings);
  const [emailError, setEmailError] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveClick = async () => {
    const sanitizedEmail = sanitizeEmail(settings.supportEmail);
    const emailValidationResult = validateEmail(sanitizedEmail);
    if (!emailValidationResult.isValid) {
      setEmailError(emailValidationResult.error);
      return;
    }

    setIsSaving(true);

    const [resp, error] = await updateInvestorPortalSettings({
      supportEmail: sanitizedEmail,
      supportPhone: settings.supportPhone.trim(),
    });

    setIsSaving(false);

    if (error) {
      logError(error, "[SupportInfoEditor] updateInvestorPortalSettings");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Settings saved successfully");
    setSettings(resp.settings);
    setSavedSettings(resp.settings);
  };

  const handleEmailChange = (value: string) => {
    setSettings({ ...settings, supportEmail: value });
    if (emailError) {
      setEmailError(undefined);
    }
  };

  const handleEmailBlur = () => {
    const sanitizedEmail = sanitizeEmail(settings.supportEmail);
    const emailValidationResult = validateEmail(sanitizedEmail);
    setSettings({ ...settings, supportEmail: sanitizedEmail });
    setEmailError(emailValidationResult.isValid ? undefined : emailValidationResult.error);
  };

  const handlePhoneChange = (value: string) => {
    setSettings({ ...settings, supportPhone: value });
  };

  const handleResetClick = () => {
    setSettings(savedSettings);
    setEmailError(undefined);
  };

  const hasPermissionsToEdit = hasPermissions(["ManageInvestorPortalSettings"]);

  const disableSave = !hasPermissionsToEdit || !!emailError || deepEqual(savedSettings, settings);

  return (
    <Stack spacing={3} sx={{ py: 2.5 }}>
      <Stack spacing={2}>
        <Typography color="textSecondary">
          Add Help Center e-mail that will be displayed inside Investor Portal and on Sign-in page.
        </Typography>
        <TextField
          label="Email"
          disabled={!hasPermissionsToEdit}
          value={settings.supportEmail}
          onChange={({ target }) => handleEmailChange(target.value)}
          onBlur={handleEmailBlur}
          error={!!emailError}
          helperText={emailError}
        />
      </Stack>
      <Stack spacing={2}>
        <Typography color="textSecondary">
          Add Help Center phone number that will be displayed inside Investor Portal. (Optional)
        </Typography>
        <PhoneTextField
          value={settings.supportPhone}
          onValueChange={handlePhoneChange}
          disabled={!hasPermissionsToEdit}
        />
      </Stack>
      {hasPermissionsToEdit && (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            sx={{ width: theme.spacing(10) }}
            disabled={disableSave}
            loading={isSaving}
            onClick={handleSaveClick}
          >
            Save
          </Button>
          <Button disabled={disableSave} variant="text" color="secondary" onClick={handleResetClick}>
            Cancel
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default SupportInfoEditor;
